import {
  AlertTriangle,
  Brain,
  HeartHandshake,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import type { HouseholdIntelligenceResponse } from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";

type Props = {
  intelligence: HouseholdIntelligenceResponse;
};

export function HouseholdHealthCard({ intelligence }: Props) {
  const visual = getRiskVisual(intelligence.risk_level);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-6 border-b border-slate-800 p-6 lg:flex-row lg:items-center">
        <div
          className={`relative flex h-40 w-40 shrink-0 items-center justify-center rounded-full border-[10px] ${visual.ringClass}`}
        >
          <div className="text-center">
            <p className={`text-5xl font-bold ${visual.textClass}`}>
              {intelligence.health_score}
            </p>

            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Health
            </p>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <div className={`rounded-xl p-3 ${visual.iconClass}`}>
              <Brain size={24} />
            </div>

            <div>
              <p className="text-sm font-medium text-slate-400">
                MinistryIQ Intelligence
              </p>

              <h2 className="text-2xl font-bold text-white">
                Household Health Overview
              </h2>
            </div>

            <span
              className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${visual.badgeClass}`}
            >
              {formatRiskLevel(intelligence.risk_level)}
            </span>
          </div>

          <p className="mt-5 max-w-3xl text-base leading-7 text-slate-300">
            {intelligence.summary}
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <MetricPill
              icon={<Users size={15} />}
              label="Members"
              value={intelligence.member_count}
            />

            <MetricPill
              icon={<ShieldCheck size={15} />}
              label="Attendance"
              value={`${intelligence.attendance_rate}%`}
            />

            <MetricPill
              icon={<HeartHandshake size={15} />}
              label="Open Care"
              value={intelligence.open_care_items}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 p-6 lg:grid-cols-2">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-violet-400" />

            <h3 className="font-semibold text-white">
              What MinistryIQ Sees
            </h3>
          </div>

          <div className="mt-4">
            {intelligence.insights.length === 0 ? (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
                <p className="font-medium text-emerald-300">
                  No significant household concerns detected
                </p>

                <p className="mt-2 text-sm leading-6 text-slate-400">
                  MinistryIQ has not identified any immediate household-level
                  risk signals.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
                <div className="flex gap-3">
                  <AlertTriangle
                    size={19}
                    className="mt-0.5 shrink-0 text-amber-400"
                  />

                  <div>
                    <p className="font-semibold text-white">
                      {intelligence.insights[0].title}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-300">
                      {intelligence.insights[0].description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-400" />

            <h3 className="font-semibold text-white">
              Recommended Next Step
            </h3>
          </div>

          <div className="mt-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-5">
            <p className="font-semibold text-white">
              Household pastoral response
            </p>

            <p className="mt-3 leading-6 text-slate-300">
              {intelligence.recommended_action}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function MetricPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-slate-800 bg-slate-950/50 px-3 py-2 text-xs">
      <span className="text-slate-500">{icon}</span>
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-white">{value}</span>
    </div>
  );
}

function getRiskVisual(level: string) {
  switch (level) {
    case "HEALTHY":
      return {
        textClass: "text-emerald-400",
        ringClass: "border-emerald-500/80",
        badgeClass: "bg-emerald-500/15 text-emerald-300",
        iconClass: "bg-emerald-500/15 text-emerald-400",
      };

    case "WATCH":
      return {
        textClass: "text-amber-400",
        ringClass: "border-amber-500/80",
        badgeClass: "bg-amber-500/15 text-amber-300",
        iconClass: "bg-amber-500/15 text-amber-400",
      };

    case "AT_RISK":
      return {
        textClass: "text-orange-400",
        ringClass: "border-orange-500/80",
        badgeClass: "bg-orange-500/15 text-orange-300",
        iconClass: "bg-orange-500/15 text-orange-400",
      };

    default:
      return {
        textClass: "text-rose-400",
        ringClass: "border-rose-500/80",
        badgeClass: "bg-rose-500/15 text-rose-300",
        iconClass: "bg-rose-500/15 text-rose-400",
      };
  }
}

function formatRiskLevel(level: string) {
  return level.replaceAll("_", " ");
}